import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';

const KycVerification = () => {
  const navigate = useNavigate();
  const { userid } = useParams(); // Get userid from URL parameter
  const location = useLocation();
  
  const [aadhar, setAadhar] = useState('');
  const [pan, setPan] = useState('');
  const [edit, setEdit] = useState(false);
  const [kyc_status, setKyc_status] = useState('');
  const [images, setImages] = useState({
    aadharFront: '',
    aadharBack: '',
    panCard: '',
    selfie: ''
  });
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [userId, setUserId] = useState('');
  
  // Bank details states
  const [bankDetails, setBankDetails] = useState({
    bank_name: '',
    ifsc_code: '',
    account_number: '',
    account_holder_name: ''
  });

  useEffect(() => {
    // Debug: Log all possible ways to get userid
    console.log('useParams userid:', userid);
    console.log('location.pathname:', location.pathname);
    console.log('location.search:', location.search);
    console.log('location.state:', location.state);
    
    // Try to get userid from multiple sources
    let extractedUserId = '';
    
    // 1. Try from URL params (/:userid)
    if (userid) {
      extractedUserId = userid;
    }
    
    // 2. Try from query params (?userid=37)
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get('userid')) {
      extractedUserId = queryParams.get('userid');
    }
    
    // 3. Try from navigation state
    if (location.state?.userid) {
      extractedUserId = location.state.userid;
    }
    
    // 4. Try from localStorage
    if (!extractedUserId) {
      const storedUserId = localStorage.getItem('userid');
      if (storedUserId) {
        extractedUserId = storedUserId;
      }
    }
    
    // 5. Try to extract from pathname (if it contains numbers at the end)
    if (!extractedUserId) {
      const pathParts = location.pathname.split('/');
      const lastPart = pathParts[pathParts.length - 1];
      if (lastPart && /^\d+$/.test(lastPart)) {
        extractedUserId = lastPart;
      }
    }
    
    console.log('Extracted User ID:', extractedUserId);
    
    if (extractedUserId) {
      setUserId(extractedUserId);
      fetchUserDetails(extractedUserId);
    } else {
      console.error('No userid found in:', {
        params: userid,
        search: location.search,
        state: location.state,
        pathname: location.pathname
      });
      alert('User ID not found. Please login again.');
      navigate('/login');
    }
  }, [userid, location, navigate]);

  const fetchUserDetails = async (id) => {
    try {
      console.log('Fetching user details for ID:', id);
      const response = await fetch(`https://octafxx.in/Rest/user_details_api/${id}`);
      const data = await response.json();
      console.log('User details response:', data);
      
      setAadhar(data.aadhar || '');
      setPan(data.pan_no || '');
      setKyc_status(data.kyc_status || '');
      
      if (data.aadhar_doc === '' || data.aadhar_doc2 === '' || data.pan_doc === '') {
        setEdit(true);
      }
      
      setImages({
        aadharFront: data.aadhar_doc || '',
        aadharBack: data.aadhar_doc2 || '',
        panCard: data.pan_doc || '',
        selfie: data.profile_img || ''
      });
      
      // Set bank details from the same API response
      setBankDetails({
        bank_name: data.bank_name || '',
        ifsc_code: data.ifsc || '',
        account_number: data.acc_number || ''
      });
      
    } catch (error) {
      console.error('Error fetching user details:', error);
      alert('Failed to fetch user details. Please try again.');
    }
  };

  const pickImage = async (imageType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg, image/png, image/jpg';
    
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      if (file.size > 2 * 1024 * 1024) {
        alert('File size must be less than 2MB');
        return;
      }
      
      setUploading(true);
      
      const formData = new FormData();
      formData.append('photo', file);
      
      try {
        const response = await fetch('https://octafxx.in/Rest/upload_image_api/', {
          method: 'POST',
          body: formData,
        });
        
        if (!response.ok) {
          throw new Error(`Upload failed: ${response.status}`);
        }
        
        const responseJson = await response.json();
        
        if (!responseJson.img) {
          throw new Error('Invalid server response');
        }
        
        const imageUrl = `https://octafxx.in/assets/kyc/${responseJson.img}`;
        
        setImages(prev => ({
          ...prev,
          [imageType]: imageUrl,
        }));
        
        alert(`${imageType} uploaded successfully!`);
      } catch (error) {
        console.error('Error uploading image:', error);
        alert('Failed to upload image. Please try again.');
      } finally {
        setUploading(false);
      }
    };
    
    input.click();
  };

  const handleSubmit = async () => {
    // Validate KYC fields
    if (aadhar === '' || pan === '' || images.aadharFront === '' || images.aadharBack === '' || images.panCard === '') {
      alert('Please provide all KYC details and upload documents');
      return;
    }
    
    if (aadhar.length !== 12) {
      alert('Please enter a valid 12-digit Aadhar number');
      return;
    }
    
    if (pan.length !== 10) {
      alert('Please enter a valid 10-digit PAN number');
      return;
    }

    // Validate bank details
    if (!bankDetails.bank_name || !bankDetails.ifsc_code || !bankDetails.account_number) {
      alert('Please fill all bank details');
      return;
    }

    if (bankDetails.account_number.length < 9 || bankDetails.account_number.length > 18) {
      alert('Please enter a valid account number (9-18 digits)');
      return;
    }

    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(bankDetails.ifsc_code)) {
      alert('Please enter a valid IFSC code (e.g., SBIN0001234)');
      return;
    }

    try {
      setLoading(true);
      
      // Create FormData object with all data
      const formData = new FormData();
      
      // KYC Data
      formData.append('aadhar_number', aadhar);
      formData.append('pan_number', pan);
      formData.append('aadhar_doc', images.aadharFront);
      formData.append('aadhar_doc2', images.aadharBack);
      formData.append('pan_doc', images.panCard);
      formData.append('profile_img', images.selfie);
      formData.append('user_id', userId);
      
      // Bank Data
      formData.append('bank_name', bankDetails.bank_name);
      formData.append('ifsc', bankDetails.ifsc_code);
      formData.append('acc_holder', bankDetails.account_number);

      const response = await fetch(`https://octafxx.in/Rest/web_upload_kyc_api`, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json',
        },
      });

      const responseJson = await response.json();
      
      if (responseJson.status === 0) {
        alert('KYC and Bank Details submitted successfully!');
        navigate('/dashboard');
      } else {
        alert(responseJson.msg || 'Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderDocumentUpload = (title, imageType, imageUri) => (
    <div 
      className={`rounded-3 p-3 mb-3 border-2 ${imageUri && !imageUri.includes('default') ? 'border-primary bg-primary bg-opacity-5' : 'border-secondary'}`}
      style={{ 
        width: '48%',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        backgroundColor: imageUri && !imageUri.includes('default') ? 'rgba(0, 84, 189, 0.05)' : '#FFFFFF',
        borderColor: imageUri && !imageUri.includes('default') ? '#0054bd' : '#E8E8E8',
        borderWidth: 2
      }}
      onClick={() => pickImage(imageType)}
    >
      <div className="text-center">
        {imageUri && !imageUri.includes('default') ? (
          <>
            <i className="bi bi-check-circle-fill text-primary" style={{ fontSize: 28 }}></i>
            <p className="fw-semibold mt-2 mb-1" style={{ fontSize: 13, color: '#1A1A1A' }}>
              {title} ✓
            </p>
            <img 
              src={imageUri} 
              alt={title}
              className="mt-2 rounded-2"
              style={{ width: '100%', height: 70, objectFit: 'cover', border: '1px solid #E8E8E8' }}
            />
          </>
        ) : (
          <>
            <div className="mx-auto d-flex justify-content-center align-items-center rounded-3 mb-2" style={{
              width: 48, height: 48,
              backgroundColor: 'rgba(0, 84, 189, 0.1)',
              border: '1px solid rgba(0, 84, 189, 0.2)'
            }}>
              <i className="bi bi-cloud-upload text-primary" style={{ fontSize: 28 }}></i>
            </div>
            <p className="fw-semibold mt-2 mb-1" style={{ fontSize: 14, color: '#1A1A1A' }}>
              Upload {title}
            </p>
            <p className="text-secondary" style={{ fontSize: 11, marginBottom: 0 }}>
              JPG, PNG (Max 2MB)
            </p>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#F8F9FA' }}>
      {(loading || uploading) && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999 }}>
          <div className="bg-white p-4 rounded-4 text-center shadow-lg">
            <Spinner animation="border" variant="primary" style={{ width: 40, height: 40 }} />
            <div className="mt-3 text-secondary fw-semibold">
              {uploading ? 'Uploading document...' : 'Submitting KYC & Bank Details...'}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="sticky-top bg-white border-bottom px-4 py-3 d-flex justify-content-between align-items-center" style={{ borderBottomColor: '#E8E8E8' }}>
        <button 
          onClick={() => navigate(-1)}
          className="btn p-2 rounded-2 d-flex align-items-center justify-content-center"
          style={{ backgroundColor: 'rgba(0, 84, 189, 0.1)', border: '1px solid rgba(0, 84, 189, 0.2)', width: 40, height: 40 }}
        >
          <i className="bi bi-arrow-left" style={{ fontSize: 20, color: '#0054bd' }}></i>
        </button>
        
        <h5 className="fw-bold m-0" style={{ color: '#1A1A1A', letterSpacing: 0.5 }}>
          KYC Verification
        </h5>
        
        <div style={{ width: 40 }}></div>
      </div>

      {/* Main Content */}
      <div className="container py-4 px-3" style={{ maxWidth: 600 }}>
        {/* User ID Display */}
        {userId && (
          <div className="mb-3 text-center">
            <span className="badge bg-primary bg-opacity-10 text-primary py-2 px-3 rounded-pill">
              <i className="bi bi-person-badge me-1"></i> User ID: {userId}
            </span>
          </div>
        )}

        {/* KYC Section */}
        <div className="bg-white p-4 rounded-4 shadow-sm mb-4">
          <h6 className="fw-bold mb-3" style={{ color: '#0054bd' }}>
            <i className="bi bi-person-badge me-2"></i>
            KYC Details
          </h6>

          {/* Aadhar Number */}
          <div className="mb-4">
            <label className="form-label fw-bold small text-uppercase mb-2" style={{ color: '#0054bd', letterSpacing: 0.5, fontSize: 11 }}>
              AADHAR NUMBER <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control rounded-4 py-3"
              placeholder="Enter 12-digit Aadhar number"
              maxLength={12}
              value={aadhar}
              onChange={(e) => setAadhar(e.target.value.replace(/\D/g, ''))}
              disabled={!edit}
              style={{
                borderWidth: 1.5,
                borderColor: aadhar.length > 0 ? '#0054bd' : '#E8E8E8',
                fontSize: 15,
                fontWeight: 500
              }}
            />
          </div>

          {/* Aadhar Uploads */}
          <label className="form-label fw-bold small text-uppercase mb-3" style={{ color: '#0054bd', letterSpacing: 0.5, fontSize: 11 }}>
            AADHAR CARD UPLOAD <span className="text-danger">*</span>
          </label>
          <div className="d-flex justify-content-between flex-wrap mb-3">
            {renderDocumentUpload('Aadhar Front', 'aadharFront', images.aadharFront)}
            {renderDocumentUpload('Aadhar Back', 'aadharBack', images.aadharBack)}
          </div>

          {/* PAN Number */}
          <div className="mb-4 mt-2">
            <label className="form-label fw-bold small text-uppercase mb-2" style={{ color: '#0054bd', letterSpacing: 0.5, fontSize: 11 }}>
              PAN NUMBER <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control rounded-4 py-3"
              placeholder="Enter 10-digit PAN number"
              maxLength={10}
              value={pan}
              onChange={(e) => setPan(e.target.value.toUpperCase())}
              disabled={!edit}
              style={{
                borderWidth: 1.5,
                borderColor: pan.length > 0 ? '#0054bd' : '#E8E8E8',
                fontSize: 15,
                fontWeight: 500
              }}
            />
          </div>

          {/* PAN and Selfie Uploads */}
          <label className="form-label fw-bold small text-uppercase mb-3" style={{ color: '#0054bd', letterSpacing: 0.5, fontSize: 11 }}>
            PAN CARD & SELFIE UPLOAD <span className="text-danger">*</span>
          </label>
          <div className="d-flex justify-content-between flex-wrap mb-3">
            {renderDocumentUpload('PAN Card', 'panCard', images.panCard)}
            {renderDocumentUpload('Selfie', 'selfie', images.selfie)}
          </div>
        </div>

        {/* Bank Details Section */}
        <div className="bg-white p-4 rounded-4 shadow-sm mb-4">
          <h6 className="fw-bold mb-3" style={{ color: '#10B981' }}>
            <i className="bi bi-bank me-2"></i>
            Bank Details
          </h6>

          

          <div className="mb-3">
            <label className="form-label fw-bold small text-uppercase" style={{ color: '#10B981', fontSize: 11 }}>
              Bank Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control rounded-4 py-2"
              placeholder="Enter bank name"
              value={bankDetails.bank_name}
              onChange={(e) => setBankDetails({...bankDetails, bank_name: e.target.value})}
              style={{
                borderWidth: 1.5,
                borderColor: bankDetails.bank_name ? '#10B981' : '#E8E8E8'
              }}
            />
          </div>

          <div className="mb-3">
            <label className="form-label fw-bold small text-uppercase" style={{ color: '#10B981', fontSize: 11 }}>
              Account Number <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control rounded-4 py-2"
              placeholder="Enter account number"
              value={bankDetails.account_number}
              onChange={(e) => setBankDetails({...bankDetails, account_number: e.target.value.replace(/\D/g, '')})}
              style={{
                borderWidth: 1.5,
                borderColor: bankDetails.account_number ? '#10B981' : '#E8E8E8'
              }}
            />
            <small className="text-muted">9-18 digits allowed</small>
          </div>

          <div className="mb-3">
            <label className="form-label fw-bold small text-uppercase" style={{ color: '#10B981', fontSize: 11 }}>
              IFSC Code <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className="form-control rounded-4 py-2"
              placeholder="Enter IFSC code (e.g., SBIN0001234)"
              value={bankDetails.ifsc_code}
              onChange={(e) => setBankDetails({...bankDetails, ifsc_code: e.target.value.toUpperCase()})}
              style={{
                borderWidth: 1.5,
                borderColor: bankDetails.ifsc_code ? '#10B981' : '#E8E8E8'
              }}
            />
            <small className="text-muted">Example: SBIN0001234</small>
          </div>
        </div>

        {/* Submit Button */}
        <button
          className="btn w-100 py-3 fw-bold rounded-4"
          style={{
            background: 'linear-gradient(135deg, #0054bd, #003d8a)',
            color: '#FFFFFF',
            fontSize: 16,
            letterSpacing: 1,
            border: 'none',
            transition: 'all 0.3s ease',
            boxShadow: '0 4px 14px rgba(0,84,189,0.3)'
          }}
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <Spinner animation="border" size="sm" style={{ color: '#fff' }} /> : 'SUBMIT KYC & BANK DETAILS'}
        </button>

        {/* Info Note */}
        <div className="mt-4 p-3 rounded-3" style={{ backgroundColor: 'rgba(0, 84, 189, 0.05)', border: '1px solid rgba(0, 84, 189, 0.1)' }}>
          <div className="d-flex align-items-center mb-2">
            <i className="bi bi-shield-check text-primary" style={{ fontSize: 20 }}></i>
            <span className="fw-bold ms-2" style={{ color: '#1A1A1A', fontSize: 13 }}>
              Secure & Encrypted
            </span>
          </div>
          <p className="text-secondary m-0" style={{ fontSize: 12, lineHeight: 1.5 }}>
            Your documents and bank details are securely encrypted and will only be used for verification and payment purposes.
          </p>
        </div>

        {/* KYC Status */}
        {kyc_status && (
          <div className={`mt-3 p-3 rounded-3 text-center ${kyc_status === 'approved' ? 'bg-success bg-opacity-10 border border-success' : 'bg-warning bg-opacity-10 border border-warning'}`}>
            <p className="fw-semibold m-0" style={{ color: kyc_status === 'approved' ? '#10B981' : '#F59E0B' }}>
              KYC Status: {kyc_status.toUpperCase()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default KycVerification;